/**
 * ReviewExclusiveSubscriptionUseCase
 *
 * The creator-side approve/reject action for a pending exclusive-subscription
 * request. Mirrors Flutter's
 * `SubscriptionsRepository.reviewExclusiveSubscription`
 * (`lib/data/repository/subscriptions_repository.dart:73-84`) as consumed by
 * `_reviewExclusiveSubscription(bool accept)`
 * (`lib/presentation/bottom_sheet/review_exclusive_subscription_bottom_sheet.dart:67-85`).
 *
 * No guards, by design. Unlike `ToggleExclusiveSubscriptionUseCase` (whose
 * self/blocked/suspended guards are ported from
 * `subscribe_user_bottom_sheet.dart:221-227`), the Flutter review flow has
 * none: the sheet calls straight through (`:73-77`). Authorization is the
 * server's: the cloud function derives the reviewer from `auth.uid`
 * (`review_exclusive_subscription.ts:18`) behind an `unauthenticated` gate
 * (`:10-15`) and only touches a notification under the caller's own subtree, so
 * a client-side gate would add nothing.
 *
 * The boolean result is passed through, but note Flutter's caller discards it
 * (`:73`, no branching on the return). A `false` means either a transport
 * failure *or* that the request was already cancelled before the reviewer
 * answered (`review_exclusive_subscription.ts:26-29`) — the two are
 * indistinguishable here, which is why no caller may render an error from it.
 *
 * The data layer already logs and swallows errors, resolving `false` on
 * failure, so this use case adds no try/catch of its own (same reasoning as
 * `toggleExclusiveSubscriptionUseCase.ts:21-23`).
 */
import type { ReviewExclusiveSubscriptionInput } from '@/domain/input/exclusiveContentSubscription/reviewExclusiveSubscriptionInput';
import {
  ExclusiveContentSubscriptionActionsService,
  exclusiveContentSubscriptionActionsService,
} from '@/domain/services/exclusiveContentSubscription/exclusiveContentSubscriptionActionsService';

export class ReviewExclusiveSubscriptionUseCase {
  private service: ExclusiveContentSubscriptionActionsService;

  constructor(service?: ExclusiveContentSubscriptionActionsService) {
    this.service = service ?? exclusiveContentSubscriptionActionsService;
  }

  async execute(input: ReviewExclusiveSubscriptionInput): Promise<boolean> {
    const { requestNotificationId, userId, accept } = input;

    return this.service.reviewSubscription(requestNotificationId, userId, accept);
  }
}

export const createReviewExclusiveSubscriptionUseCase = (): ReviewExclusiveSubscriptionUseCase => {
  return new ReviewExclusiveSubscriptionUseCase();
};
